<script>
import { useDebounceFn } from '@vueuse/core';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CampaignContactGroupsAPI from 'dashboard/api/campaignContactGroups';
import CampaignContactsAPI from 'dashboard/api/campaignContacts';
import Spinner from 'shared/components/Spinner.vue';

const CONTACT_FILTER_FIELDS = [
  { value: 'name', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.NAME' },
  { value: 'email', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.EMAIL' },
  { value: 'phone_number', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.PHONE' },
  { value: 'identifier', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.IDENTIFIER' },
  { value: 'company', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.COMPANY' },
  { value: 'city', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.CITY' },
];

export default {
  name: 'CampaignGroupsManager',
  components: { Spinner },
  props: {
    selectedContacts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      groups: [],
      groupsLoading: false,
      editingGroup: null,
      editingLoading: false,
      savingGroup: false,
      addContactsLoading: false,
      memberLoading: {},
      deleteLoading: false,
      deleteTargetId: null,
      showEditModal: false,
      selectedContactsDisplayLimit: 50,
      editingContactsScrollObserver: null,
      showAddContactsSection: false,
      contactsToAdd: [],
      contactsToAddLoading: false,
      contactsToAddSearch: '',
      contactsToAddFilters: {
        search: '',
        labelIds: [],
        field: 'name',
        fieldValue: '',
      },
      contactsToAddSearchInput: '',
      contactsToAddSelected: [],
      contactsToAddPage: 1,
      contactsToAddPerPage: 25,
      contactsToAddMeta: {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
      },
      contactsToAddScrollObserver: null,
      contactsToAddScrollSentinel: null,
      contactsToAddSelectedMap: {},
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
    }),
    selectedContactIds() {
      return this.selectedContacts.map(contact => contact.id);
    },
    selectedContactsCount() {
      return this.selectedContactIds.length;
    },
    contactsNotInGroup() {
      if (!this.editingGroup) {
        return [];
      }
      const existingIds = new Set((this.editingGroup.contacts || []).map(contact => contact.id));
      return this.selectedContacts.filter(contact => !existingIds.has(contact.id));
    },
    canAddSelected() {
      return this.contactsNotInGroup.length > 0;
    },
    displayedEditingContacts() {
      if (!this.editingGroup || !this.editingGroup.contacts) return [];
      return this.editingGroup.contacts.slice(0, this.selectedContactsDisplayLimit);
    },
    hasMoreEditingContacts() {
      if (!this.editingGroup || !this.editingGroup.contacts) return false;
      return this.editingGroup.contacts.length > this.selectedContactsDisplayLimit;
    },
    contactsToAddSelected() {
      // Garantir reatividade
      return Object.keys(this.contactsToAddSelectedMap).map(key => this.contactsToAddSelectedMap[key]);
    },
    contactsToAddSelectedCount() {
      // Garantir reatividade contando as chaves
      return Object.keys(this.contactsToAddSelectedMap).length;
    },
    contactsNotInGroupFiltered() {
      if (!this.editingGroup) return [];
      const existingIds = new Set((this.editingGroup.contacts || []).map(contact => contact.id));
      return this.contactsToAdd.filter(contact => !existingIds.has(contact.id));
    },
    areAllContactsToAddSelected() {
      if (!this.contactsToAdd.length) return false;
      return this.contactsToAddNotInGroup.every(contact => this.contactsToAddSelectedMap[contact.id]);
    },
    areSomeContactsToAddSelected() {
      if (!this.contactsToAdd.length) return false;
      return this.contactsToAddNotInGroup.some(contact => this.contactsToAddSelectedMap[contact.id]);
    },
    contactsToAddNotInGroup() {
      if (!this.editingGroup) return this.contactsToAdd;
      const existingIds = new Set((this.editingGroup.contacts || []).map(contact => contact.id));
      return this.contactsToAdd.filter(contact => !existingIds.has(contact.id));
    },
    hasMoreContactsToAdd() {
      return this.contactsToAddMeta.current_page < this.contactsToAddMeta.total_pages;
    },
    filterFields() {
      return CONTACT_FILTER_FIELDS;
    },
  },
  created() {
    this.debouncedContactsToAddSearch = useDebounceFn(value => {
      this.contactsToAddFilters.search = value;
      this.resetAndFetchContactsToAdd();
    }, 300);
    this.debouncedContactsToAddFieldValue = useDebounceFn(value => {
      this.contactsToAddFilters.fieldValue = value;
      this.resetAndFetchContactsToAdd();
    }, 300);
  },
  mounted() {
    this.fetchGroups();
    if (!this.labels.length) {
      this.$store.dispatch('labels/get');
    }
  },
  watch: {
    showEditModal(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.setupEditingContactsScroll();
        });
      } else {
        if (this.editingContactsScrollObserver) {
          this.editingContactsScrollObserver.disconnect();
        }
        if (this.contactsToAddScrollObserver) {
          this.contactsToAddScrollObserver.disconnect();
        }
        this.showAddContactsSection = false;
        this.resetContactsToAdd();
      }
    },
    showAddContactsSection: {
      handler(newValue) {
        if (newValue && this.contactsToAdd.length === 0 && this.editingGroup) {
          // Buscar contatos quando a seção for aberta pela primeira vez
          this.$nextTick(() => {
            this.resetAndFetchContactsToAdd();
          });
        }
      },
      immediate: false,
    },
    editingGroup: {
      handler() {
        if (this.editingGroup && this.showEditModal) {
          this.$nextTick(() => {
            this.updateEditingContactsObserver();
            // Ajustar limite se necessário
            if (this.editingGroup.contacts && this.editingGroup.contacts.length < this.selectedContactsDisplayLimit) {
              this.selectedContactsDisplayLimit = Math.max(50, this.editingGroup.contacts.length);
            }
          });
        }
      },
      deep: true,
    },
    'contactsToAddFilters.labelIds': {
      handler() {
        if (this.showAddContactsSection && this.editingGroup) {
          this.resetAndFetchContactsToAdd();
        }
      },
      deep: true,
    },
  },
  beforeUnmount() {
    if (this.editingContactsScrollObserver) {
      this.editingContactsScrollObserver.disconnect();
    }
    if (this.contactsToAddScrollObserver) {
      this.contactsToAddScrollObserver.disconnect();
    }
  },
  methods: {
    async fetchGroups() {
      this.groupsLoading = true;
      try {
        const { data } = await CampaignContactGroupsAPI.list();
        this.groups = data.groups || [];
        this.$emit('groups-updated');
        this.$emit('groups-count-changed', this.groups.length);
        if (this.editingGroup) {
          const updated = this.groups.find(group => group.id === this.editingGroup.id);
          if (updated) {
            await this.loadGroup(updated.id);
          } else {
            this.resetEditor();
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.groupsLoading = false;
      }
    },
    async loadGroup(groupId) {
      this.editingLoading = true;
      try {
        const { data } = await CampaignContactGroupsAPI.show(groupId);
        this.setEditorGroup(data.group);
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ERROR_LOAD');
        useAlert(message);
      } finally {
        this.editingLoading = false;
      }
    },
    setEditorGroup(group) {
      if (!group) {
        this.resetEditor();
        return;
      }
      this.editingGroup = {
        id: group.id,
        name: group.name,
        description: group.description,
        contacts_count: group.contacts_count,
        contacts: group.contacts || [],
      };
    },
    resetEditor() {
      this.editingGroup = null;
      this.memberLoading = {};
      this.addContactsLoading = false;
      this.selectedContactsDisplayLimit = 50;
      this.resetContactsToAdd();
    },
    resetContactsToAdd() {
      this.contactsToAdd = [];
      this.contactsToAddSelectedMap = {};
      this.contactsToAddMeta = { current_page: 0, total_pages: 1, total_count: 0 };
      this.contactsToAddFilters = {
        search: '',
        labelIds: [],
        field: 'name',
        fieldValue: '',
      };
      this.contactsToAddSearchInput = '';
      if (this.contactsToAddScrollObserver) {
        this.contactsToAddScrollObserver.disconnect();
      }
      if (this.contactsToAddScrollSentinel && this.contactsToAddScrollSentinel.parentNode) {
        this.contactsToAddScrollSentinel.parentNode.removeChild(this.contactsToAddScrollSentinel);
      }
    },
    async openEditor(group) {
      if (!group) {
        return;
      }
      this.showEditModal = true;
      await this.loadGroup(group.id);
    },
    closeEditModal() {
      this.showEditModal = false;
      this.resetEditor();
    },
    async saveGroupDetails() {
      if (!this.editingGroup || this.savingGroup) {
        return;
      }
      this.savingGroup = true;
      try {
        const payload = {
          campaign_contact_group: {
            name: this.editingGroup.name,
            description: this.editingGroup.description,
          },
        };
        const { data } = await CampaignContactGroupsAPI.update(this.editingGroup.id, payload);
        this.setEditorGroup(data.group);
        await this.fetchGroups();
        this.$emit('groups-updated');
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.UPDATE_SUCCESS'));
        // Ajustar limite de exibição
        if (this.editingGroup.contacts.length < this.selectedContactsDisplayLimit) {
          this.selectedContactsDisplayLimit = Math.max(50, this.editingGroup.contacts.length);
        }
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.UPDATE_ERROR');
        useAlert(message);
      } finally {
        this.savingGroup = false;
      }
    },
    async removeMember(contactId) {
      if (!this.editingGroup || this.memberLoading[contactId]) {
        return;
      }
      this.memberLoading[contactId] = true;
      try {
        const payload = {
          campaign_contact_group: {
            remove_contact_ids: [contactId],
          },
        };
        const { data } = await CampaignContactGroupsAPI.update(this.editingGroup.id, payload);
        this.setEditorGroup(data.group);
        await this.fetchGroups();
        this.$emit('groups-updated');
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.REMOVE_SUCCESS'));
        // Ajustar limite de exibição
        if (this.editingGroup.contacts.length < this.selectedContactsDisplayLimit) {
          this.selectedContactsDisplayLimit = Math.max(50, this.editingGroup.contacts.length);
        }
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.REMOVE_ERROR');
        useAlert(message);
      } finally {
        delete this.memberLoading[contactId];
      }
    },
    async addSelectedContactsToGroup() {
      if (!this.editingGroup || !this.canAddSelected || this.addContactsLoading) {
        return;
      }

      // Verificar quais contatos já estão no grupo
      const existingIds = new Set((this.editingGroup.contacts || []).map(contact => contact.id));
      const contactsAlreadyInGroup = this.contactsNotInGroup.filter(contact => 
        existingIds.has(contact.id)
      );
      const contactsToAdd = this.contactsNotInGroup.filter(contact => 
        !existingIds.has(contact.id)
      );

      // Se todos os contatos já estão no grupo
      if (contactsAlreadyInGroup.length > 0 && contactsToAdd.length === 0) {
        const contactNames = contactsAlreadyInGroup
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id)
          .slice(0, 3);
        const moreCount = contactsAlreadyInGroup.length - contactNames.length;
        let message = this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ALL_CONTACTS_ALREADY_IN_GROUP', {
          count: contactsAlreadyInGroup.length,
        });
        if (contactNames.length > 0) {
          message += `: ${contactNames.join(', ')}`;
          if (moreCount > 0) {
            message += ` ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.AND_MORE', { count: moreCount })}`;
          }
        }
        useAlert(message);
        return;
      }

      // Se alguns contatos já estão no grupo, mostrar mensagem informativa
      if (contactsAlreadyInGroup.length > 0 && contactsToAdd.length > 0) {
        const contactNames = contactsAlreadyInGroup
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id)
          .slice(0, 3);
        const moreCount = contactsAlreadyInGroup.length - contactNames.length;
        let warningMessage = this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.SOME_CONTACTS_ALREADY_IN_GROUP', {
          count: contactsAlreadyInGroup.length,
        });
        if (contactNames.length > 0) {
          warningMessage += `: ${contactNames.join(', ')}`;
          if (moreCount > 0) {
            warningMessage += ` ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.AND_MORE', { count: moreCount })}`;
          }
        }
        warningMessage += `. ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADDING_REMAINING', {
          count: contactsToAdd.length,
        })}`;
        useAlert(warningMessage);
      }

      // Adicionar apenas os contatos que não estão no grupo
      if (contactsToAdd.length === 0) {
        return;
      }

      this.addContactsLoading = true;
      try {
        const payload = {
          campaign_contact_group: {
            add_contact_ids: contactsToAdd.map(contact => contact.id),
          },
        };
        const { data } = await CampaignContactGroupsAPI.update(this.editingGroup.id, payload);
        this.setEditorGroup(data.group);
        await this.fetchGroups();
        this.$emit('groups-updated');
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_SUCCESS'));
        // Ajustar limite de exibição se necessário
        if (this.editingGroup.contacts.length < this.selectedContactsDisplayLimit) {
          this.selectedContactsDisplayLimit = Math.max(50, this.editingGroup.contacts.length);
        }
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_ERROR');
        useAlert(message);
      } finally {
        this.addContactsLoading = false;
      }
    },
    async addSelectedContactsFromSearch() {
      if (!this.editingGroup || this.addContactsLoading) {
        return;
      }
      
      // Obter contatos selecionados diretamente do map para garantir que temos os dados atualizados
      const selectedContacts = Object.keys(this.contactsToAddSelectedMap)
        .map(key => this.contactsToAddSelectedMap[key])
        .filter(contact => contact && contact.id);
      
      if (!selectedContacts || selectedContacts.length === 0) {
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_ERROR'));
        return;
      }

      // Verificar quais contatos já estão no grupo
      const existingIds = new Set((this.editingGroup.contacts || []).map(contact => contact.id));
      const contactsAlreadyInGroup = selectedContacts.filter(contact => 
        existingIds.has(contact.id)
      );
      const contactsToAdd = selectedContacts.filter(contact => 
        !existingIds.has(contact.id)
      );

      // Se todos os contatos já estão no grupo
      if (contactsAlreadyInGroup.length > 0 && contactsToAdd.length === 0) {
        const contactNames = contactsAlreadyInGroup
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id)
          .slice(0, 3);
        const moreCount = contactsAlreadyInGroup.length - contactNames.length;
        let message = this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ALL_CONTACTS_ALREADY_IN_GROUP', {
          count: contactsAlreadyInGroup.length,
        });
        if (contactNames.length > 0) {
          message += `: ${contactNames.join(', ')}`;
          if (moreCount > 0) {
            message += ` ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.AND_MORE', { count: moreCount })}`;
          }
        }
        useAlert(message);
        return;
      }

      // Se alguns contatos já estão no grupo, mostrar mensagem informativa
      if (contactsAlreadyInGroup.length > 0 && contactsToAdd.length > 0) {
        const contactNames = contactsAlreadyInGroup
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id)
          .slice(0, 3);
        const moreCount = contactsAlreadyInGroup.length - contactNames.length;
        let warningMessage = this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.SOME_CONTACTS_ALREADY_IN_GROUP', {
          count: contactsAlreadyInGroup.length,
        });
        if (contactNames.length > 0) {
          warningMessage += `: ${contactNames.join(', ')}`;
          if (moreCount > 0) {
            warningMessage += ` ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.AND_MORE', { count: moreCount })}`;
          }
        }
        warningMessage += `. ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADDING_REMAINING', {
          count: contactsToAdd.length,
        })}`;
        useAlert(warningMessage);
      }

      // Adicionar apenas os contatos que não estão no grupo
      if (contactsToAdd.length === 0) {
        return;
      }

      this.addContactsLoading = true;
      try {
        const contactIds = contactsToAdd.map(contact => contact.id);
        const payload = {
          campaign_contact_group: {
            add_contact_ids: contactIds,
          },
        };
        const { data } = await CampaignContactGroupsAPI.update(this.editingGroup.id, payload);
        this.setEditorGroup(data.group);
        await this.fetchGroups();
        this.$emit('groups-updated');
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_SUCCESS'));
      // Limpar seleção
      this.contactsToAddSelectedMap = {};
      // Remover contatos adicionados da lista de busca
      const addedIds = new Set(contactIds);
      this.contactsToAdd = this.contactsToAdd.filter(contact => !addedIds.has(contact.id));
      // Ajustar limite de exibição se necessário
      if (this.editingGroup && this.editingGroup.contacts && this.editingGroup.contacts.length < this.selectedContactsDisplayLimit) {
        this.selectedContactsDisplayLimit = Math.max(50, this.editingGroup.contacts.length);
      }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error adding contacts to group:', error);
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_ERROR');
        useAlert(message);
      } finally {
        this.addContactsLoading = false;
      }
    },
    async deleteGroup(group) {
      if (!group || this.deleteLoading) {
        return;
      }
      const confirmed = window.confirm(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.DELETE_CONFIRM'));
      if (!confirmed) {
        return;
      }
      this.deleteLoading = true;
      this.deleteTargetId = group.id;
      try {
        await CampaignContactGroupsAPI.delete(group.id);
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.DELETE_SUCCESS'));
        if (this.editingGroup && this.editingGroup.id === group.id) {
          this.resetEditor();
        }
        await this.fetchGroups();
        this.$emit('groups-updated');
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.DELETE_ERROR');
        useAlert(message);
      } finally {
        this.deleteLoading = false;
        this.deleteTargetId = null;
      }
    },
    setupEditingContactsScroll() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector('.editing-contacts-scroll-container');
        if (!scrollContainer) return;

        const observerOptions = {
          root: scrollContainer,
          rootMargin: '50px',
          threshold: 0.1,
        };

        const observerCallback = (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.hasMoreEditingContacts) {
              this.selectedContactsDisplayLimit += 50;
            }
          });
        };

        this.editingContactsScrollObserver = new IntersectionObserver(observerCallback, observerOptions);
        this.updateEditingContactsObserver();
      });
    },
    updateEditingContactsObserver() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector('.editing-contacts-scroll-container');
        if (!scrollContainer || !this.editingContactsScrollObserver) return;

        const sentinel = scrollContainer.querySelector('.editing-contacts-sentinel');
        if (sentinel) {
          if (this.hasMoreEditingContacts) {
            this.editingContactsScrollObserver.observe(sentinel);
          } else {
            this.editingContactsScrollObserver.unobserve(sentinel);
          }
        }
      });
    },
    resetAndFetchContactsToAdd() {
      this.contactsToAdd = [];
      this.contactsToAddMeta = { current_page: 0, total_pages: 1, total_count: 0 };
      this.fetchContactsToAdd();
    },
    async fetchContactsToAdd() {
      if (this.contactsToAddLoading) return;
      if (!this.editingGroup) return;
      
      const nextPage = this.contactsToAddMeta.current_page + 1;
      
      this.contactsToAddLoading = true;

      try {
        const params = {
          page: nextPage,
          per_page: this.contactsToAddPerPage,
          search: this.contactsToAddFilters.search || undefined,
          field: this.contactsToAddFilters.field || undefined,
          field_value: this.contactsToAddFilters.fieldValue || undefined,
          label_ids: this.contactsToAddFilters.labelIds.map(label => label.id || label).filter(Boolean)
        };
        const { data } = await CampaignContactsAPI.list(params);
        const newContacts = data.contacts || [];
        
        // Filtrar contatos que já estão no grupo
        const existingIds = new Set((this.editingGroup?.contacts || []).map(contact => contact.id));
        const filteredContacts = newContacts.filter(contact => !existingIds.has(contact.id));
        
        // Adicionar novos contatos à lista existente
        this.contactsToAdd = [...this.contactsToAdd, ...filteredContacts];
        this.contactsToAddMeta = data.meta || { current_page: nextPage, total_pages: 1, total_count: 0 };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching contacts to add:', error);
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_ERROR'));
      } finally {
        this.contactsToAddLoading = false;
        this.$nextTick(() => {
          this.setupContactsToAddScroll();
        });
      }
    },
    setupContactsToAddScroll() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector('.contacts-to-add-scroll-container');
        if (!scrollContainer) return;

        if (!this.contactsToAddScrollObserver) {
          const observerOptions = {
            root: scrollContainer,
            rootMargin: '100px',
            threshold: 0.1,
          };

          const observerCallback = (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && this.hasMoreContactsToAdd && !this.contactsToAddLoading) {
                this.fetchContactsToAdd();
              }
            });
          };

          this.contactsToAddScrollObserver = new IntersectionObserver(observerCallback, observerOptions);
        }
        
        // Observar quando os contatos mudam
        const tbody = scrollContainer.querySelector('tbody');
        if (!tbody) return;
        
        // Remover sentinela anterior
        if (this.contactsToAddScrollSentinel) {
          this.contactsToAddScrollObserver.unobserve(this.contactsToAddScrollSentinel);
          if (this.contactsToAddScrollSentinel.parentNode) {
            this.contactsToAddScrollSentinel.parentNode.removeChild(this.contactsToAddScrollSentinel);
          }
        }
        
        // Criar novo sentinela no final do tbody
        const sentinel = document.createElement('tr');
        sentinel.className = 'contacts-to-add-sentinel';
        sentinel.innerHTML = '<td colspan="4" style="height: 1px;"></td>';
        tbody.appendChild(sentinel);
        
        this.contactsToAddScrollSentinel = sentinel;
        if (this.hasMoreContactsToAdd) {
          this.contactsToAddScrollObserver.observe(sentinel);
        }
      });
    },
    toggleContactToAdd(contact) {
      if (this.contactsToAddSelectedMap[contact.id]) {
        delete this.contactsToAddSelectedMap[contact.id];
      } else {
        this.contactsToAddSelectedMap[contact.id] = contact;
      }
      // Atualizar o estado do checkbox "selecionar todos"
      this.updateSelectAllContactsToAddCheckboxState();
    },
    isContactToAddSelected(contactId) {
      return Boolean(this.contactsToAddSelectedMap[contactId]);
    },
    toggleSelectAllContactsToAdd() {
      const allSelected = this.areAllContactsToAddSelected;
      this.contactsToAddNotInGroup.forEach(contact => {
        if (allSelected) {
          if (this.contactsToAddSelectedMap[contact.id]) {
            delete this.contactsToAddSelectedMap[contact.id];
          }
        } else {
          if (!this.contactsToAddSelectedMap[contact.id]) {
            this.contactsToAddSelectedMap[contact.id] = contact;
          }
        }
      });
      // Atualizar o estado do checkbox "selecionar todos"
      this.updateSelectAllContactsToAddCheckboxState();
    },
    onContactsToAddSearchInput(event) {
      const value = event.target.value;
      this.contactsToAddSearchInput = value;
      this.debouncedContactsToAddSearch(value);
    },
    onContactsToAddFieldValueInput(event) {
      const value = event.target.value;
      this.debouncedContactsToAddFieldValue(value);
    },
    contactDisplayValue(contact) {
      return contact.name || contact.phone_number || contact.email || contact.identifier || contact.id;
    },
    contactFieldLabel(field) {
      const option = this.filterFields.find(item => item.value === field);
      if (!option) {
        return field;
      }
      return this.$t(option.labelKey);
    },
    updateSelectAllContactsToAddCheckboxState() {
      this.$nextTick(() => {
        if (this.$refs.selectAllContactsToAddCheckbox) {
          const isIndeterminate = this.areSomeContactsToAddSelected && !this.areAllContactsToAddSelected;
          this.$refs.selectAllContactsToAddCheckbox.indeterminate = isIndeterminate;
        }
      });
    },
  },
};
</script>

<template>
  <div class="campaign-groups-manager space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-base font-semibold text-slate-800 dark:text-slate-200">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.TITLE') }}
      </h3>
      <p class="text-xs text-slate-500 dark:text-slate-400">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.SUBTITLE') }}
      </p>
    </div>

    <div class="border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 overflow-hidden">
      <table class="min-w-full text-sm">
        <thead class="bg-slate-50 dark:bg-slate-800 text-left">
          <tr>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.COLUMNS.NAME') }}
            </th>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.COLUMNS.COUNT') }}
            </th>
            <th class="px-4 py-2 text-right text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.COLUMNS.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="groupsLoading">
            <td colspan="3" class="px-4 py-6 text-center">
              <Spinner size="20" class="inline-block" />
              <span class="ml-2 text-sm text-slate-600 dark:text-slate-300">
                {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.LOADING') }}
              </span>
            </td>
          </tr>
          <tr v-else-if="!groups.length">
            <td colspan="3" class="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EMPTY') }}
            </td>
          </tr>
          <tr
            v-else
            v-for="group in groups"
            :key="group.id"
            class="border-t border-slate-200 dark:border-slate-700"
          >
            <td class="px-4 py-2 text-slate-700 dark:text-slate-200">
              {{ group.name }}
            </td>
            <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ group.contacts_count }}
            </td>
            <td class="px-4 py-2 text-right">
              <div class="inline-flex items-center justify-end gap-1">
                <woot-button
                  v-tooltip.top-end="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ACTIONS.EDIT')"
                  variant="hollow"
                  size="small"
                  type="button"
                  icon="edit"
                  color-scheme="secondary"
                  :title="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ACTIONS.EDIT')"
                  @click.prevent="openEditor(group)"
                />
                <woot-button
                  v-tooltip.top-end="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ACTIONS.DELETE')"
                  variant="hollow"
                  size="small"
                  type="button"
                  icon="delete"
                  color-scheme="alert"
                  :title="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ACTIONS.DELETE')"
                  :is-loading="deleteLoading && deleteTargetId === group.id"
                  @click.prevent="deleteGroup(group)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal de Edição -->
    <woot-modal :show.sync="showEditModal" :on-close="closeEditModal">
      <div v-if="editingLoading" class="flex items-center justify-center gap-2 p-8 text-sm text-slate-600 dark:text-slate-300">
        <Spinner size="20" />
        <span>{{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.LOADING') }}</span>
      </div>
      <div v-else-if="editingGroup" class="space-y-3 p-3">
        <woot-modal-header
          :header-title="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.MODAL_TITLE', { name: editingGroup.name })"
          :header-content="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.MODAL_DESCRIPTION')"
        />

        <div class="grid gap-2 md:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label class="woot-form-label text-sm">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.NAME_LABEL') }}
            </label>
            <input
              v-model="editingGroup.name"
              type="text"
              class="woot-input"
              :placeholder="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.NAME_PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="woot-form-label text-sm">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.DESCRIPTION_LABEL') }}
            </label>
            <textarea
              v-model="editingGroup.description"
              rows="2"
              class="woot-input"
              :placeholder="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.DESCRIPTION_PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <label class="woot-form-label text-sm">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.MEMBERS_TITLE', { count: (editingGroup.contacts || []).length }) }}
            </label>
            <div class="flex gap-2">
              <woot-button
                variant="secondary"
                size="small"
                type="button"
                @click.prevent="showAddContactsSection = !showAddContactsSection"
              >
                {{ showAddContactsSection ? $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.HIDE_ADD') : $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.SHOW_ADD') }}
              </woot-button>
              <woot-button
                v-if="canAddSelected"
                variant="secondary"
                size="small"
                type="button"
                :is-loading="addContactsLoading"
                :disabled="!canAddSelected"
                @click.prevent="addSelectedContactsToGroup"
              >
                {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_SELECTED', { count: contactsNotInGroup.length }) }}
              </woot-button>
            </div>
          </div>

          <!-- Seção de adicionar contatos -->
          <div v-if="showAddContactsSection" class="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2 bg-slate-50 dark:bg-slate-900">
            <h4 class="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.ADD_CONTACTS_TITLE') }}
            </h4>
            
            <!-- Filtros -->
            <div class="grid gap-2 md:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="woot-form-label text-xs">
                  {{ $t('CAMPAIGN.ADD.CONTACTS.SEARCH_LABEL') }}
                </label>
                <input
                  :value="contactsToAddSearchInput"
                  type="text"
                  class="woot-input text-sm"
                  :placeholder="$t('CAMPAIGN.ADD.CONTACTS.SEARCH_PLACEHOLDER')"
                  @input="onContactsToAddSearchInput"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="woot-form-label text-xs">
                  {{ $t('CAMPAIGN.ADD.CONTACTS.TAG_FILTER_LABEL') }}
                </label>
                <div class="multiselect-wrap--small">
                  <multiselect
                    v-model="contactsToAddFilters.labelIds"
                    :options="labels"
                    track-by="id"
                    label="title"
                    :multiple="true"
                    :searchable="true"
                    :close-on-select="false"
                    :show-labels="false"
                    :placeholder="$t('CAMPAIGN.ADD.CONTACTS.TAG_FILTER_PLACEHOLDER')"
                  />
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <label class="woot-form-label text-xs">
                  {{ $t('CAMPAIGN.ADD.CONTACTS.FIELD_FILTER_LABEL') }}
                </label>
                <select
                  v-model="contactsToAddFilters.field"
                  class="woot-input text-sm"
                  @change="resetAndFetchContactsToAdd"
                >
                  <option
                    v-for="option in filterFields"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ $t(option.labelKey) }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label class="woot-form-label text-xs">
                  {{ $t('CAMPAIGN.ADD.CONTACTS.FIELD_VALUE_LABEL') }}
                </label>
                <input
                  :value="contactsToAddFilters.fieldValue"
                  type="text"
                  class="woot-input text-sm"
                  :placeholder="$t('CAMPAIGN.ADD.CONTACTS.FIELD_VALUE_PLACEHOLDER')"
                  @input="onContactsToAddFieldValueInput"
                />
              </div>
            </div>

            <!-- Botão para buscar -->
            <div class="flex justify-end">
              <woot-button
                variant="secondary"
                size="small"
                type="button"
                :is-loading="contactsToAddLoading"
                @click.prevent="resetAndFetchContactsToAdd"
              >
                {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.SEARCH_CONTACTS') }}
              </woot-button>
            </div>

            <!-- Lista de contatos para adicionar -->
            <div v-if="contactsToAdd.length > 0 || contactsToAddLoading" class="border border-slate-200 dark:border-slate-700 rounded overflow-hidden contacts-to-add-scroll-container" style="max-height: 180px; overflow-y: auto;">
              <table class="min-w-full text-xs">
                <thead class="bg-slate-50 dark:bg-slate-800 text-left sticky top-0 z-10">
                  <tr>
                    <th class="px-2 py-1.5 w-8">
                      <input
                        v-if="contactsToAddNotInGroup.length > 0"
                        type="checkbox"
                        class="woot-checkbox"
                        :checked="areAllContactsToAddSelected"
                        @change="toggleSelectAllContactsToAdd"
                        :title="$t('CAMPAIGN.ADD.CONTACTS.SELECT_ALL')"
                      />
                    </th>
                    <th class="px-2 py-1.5 text-slate-600 dark:text-slate-300 text-xs">
                      {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.NAME') }}
                    </th>
                    <th class="px-2 py-1.5 text-slate-600 dark:text-slate-300 text-xs">
                      {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.EMAIL') }}
                    </th>
                    <th class="px-2 py-1.5 text-slate-600 dark:text-slate-300 text-xs">
                      {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.PHONE') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="contactsToAddLoading && !contactsToAdd.length">
                    <td colspan="4" class="px-4 py-4 text-center">
                      <Spinner size="16" class="inline-block" />
                      <span class="ml-2 text-xs text-slate-600 dark:text-slate-300">
                        {{ $t('CAMPAIGN.ADD.CONTACTS.LOADING') }}
                      </span>
                    </td>
                  </tr>
                  <tr
                    v-for="contact in contactsToAddNotInGroup"
                    :key="contact.id"
                    class="border-t border-slate-200 dark:border-slate-700"
                  >
                    <td class="px-2 py-1.5">
                      <input
                        type="checkbox"
                        class="woot-checkbox"
                        :checked="isContactToAddSelected(contact.id)"
                        @change="toggleContactToAdd(contact)"
                      />
                    </td>
                    <td class="px-2 py-1.5 text-slate-700 dark:text-slate-200">
                      {{ contact.name || '—' }}
                    </td>
                    <td class="px-2 py-1.5 text-slate-600 dark:text-slate-300">
                      {{ contact.email || '—' }}
                    </td>
                    <td class="px-2 py-1.5 text-slate-600 dark:text-slate-300">
                      {{ contact.phone_number || '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Botão para adicionar selecionados -->
            <div 
              v-if="contactsToAddSelectedCount > 0" 
              class="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg mt-2"
              key="add-selected-button"
            >
              <span class="text-xs text-blue-700 dark:text-blue-300">
                {{ $t('CAMPAIGN.ADD.CONTACTS.SELECTED_COUNT', { count: contactsToAddSelectedCount }) }}
              </span>
              <woot-button
                variant="primary"
                size="small"
                type="button"
                :is-loading="addContactsLoading"
                @click.prevent="addSelectedContactsFromSearch"
              >
                {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.ADD_SELECTED_TO_GROUP', { count: contactsToAddSelectedCount }) }}
              </woot-button>
            </div>
            <!-- Mensagem quando não há seleção -->
            <div 
              v-else-if="contactsToAdd.length > 0 && !contactsToAddLoading" 
              class="text-xs text-slate-500 dark:text-slate-400 text-center py-2"
              key="select-hint"
            >
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.SELECT_CONTACTS_HINT') }}
            </div>
          </div>

          <div class="border border-slate-200 dark:border-slate-700 rounded overflow-hidden editing-contacts-scroll-container" style="max-height: 250px; overflow-y: auto;">
            <div v-if="!(editingGroup.contacts || []).length" class="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.MEMBERS_EMPTY') }}
            </div>
            <ul v-else class="divide-y divide-slate-200 dark:divide-slate-700">
              <li
                v-for="member in displayedEditingContacts"
                :key="member.id"
                class="px-3 py-2 flex items-center justify-between text-sm text-slate-700 dark:text-slate-200"
              >
                <div class="flex flex-col flex-1">
                  <span class="font-medium text-xs">{{ member.name || member.phone_number || member.email || member.identifier || member.id }}</span>
                  <span class="text-xs text-slate-500 dark:text-slate-400">
                    {{ member.phone_number || member.email || '—' }}
                  </span>
                </div>
                <woot-button
                  v-tooltip.top-end="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.REMOVE_MEMBER')"
                  variant="hollow"
                  size="small"
                  type="button"
                  class="ml-2 shrink-0"
                  icon="dismiss-circle"
                  color-scheme="alert"
                  :title="$t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.REMOVE_MEMBER')"
                  :is-loading="!!memberLoading[member.id]"
                  @click.prevent="removeMember(member.id)"
                />
              </li>
              <li 
                v-if="hasMoreEditingContacts"
                class="editing-contacts-sentinel"
                style="height: 1px;"
              />
              <li v-if="hasMoreEditingContacts" class="px-3 py-1 text-center text-xs text-slate-500 dark:text-slate-400">
                {{ $t('CAMPAIGN.ADD.CONTACTS.SCROLL_TO_SEE_MORE', { remaining: editingGroup.contacts.length - selectedContactsDisplayLimit }) }}
              </li>
            </ul>
          </div>
        </div>

        <div class="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700 mt-3">
          <woot-button
            variant="clear"
            type="button"
            @click.prevent="closeEditModal"
          >
            {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.CANCEL') }}
          </woot-button>
          <woot-button
            variant="primary"
            type="button"
            :is-loading="savingGroup"
            @click.prevent="saveGroupDetails"
          >
            {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.SAVE') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
.campaign-groups-manager {
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--s-200);
  background-color: var(--white);
}

.dark .campaign-groups-manager {
  background-color: var(--s-900);
  border-color: var(--s-700);
}
</style>
